
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Search, Plus, Clock } from "lucide-react";
import { format } from "date-fns";

interface ScanHistoryProps {
  className?: string;
  onClose?: () => void;
  onSelect?: (scan: any) => void;
}

const ScanHistory = ({ className, onClose, onSelect }: ScanHistoryProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { data: scans, isLoading } = useQuery({
    queryKey: ['scan-history'],
    queryFn: async () => {
      console.log('Fetching scan history...');
      const { data, error } = await supabase
        .from('scans')
        .select('*, products(*)')
        .order('created_at', { ascending: false })
        .limit(5);

      if (error) {
        console.error('Error fetching scan history:', error);
        throw error;
      }

      console.log('Scan history fetched:', data);
      return data;
    }
  });

  const handleScanClick = (scan: any) => {
    if (onSelect) {
      onSelect(scan);
    } else {
      if (scan.product_id) {
        navigate(`/product/${scan.product_id}`);
        if (onClose) onClose();
      } else if (scan.ocr_result) {
        navigate(`/?q=${encodeURIComponent(scan.ocr_result)}`);
        if (onClose) onClose();
      }
    }
  };

  const handleAddProduct = (scan: any) => {
    navigate('/product/new', { state: { detectedText: scan.ocr_result } });
    if (onClose) onClose();
  };

  if (isLoading) {
    return (
      <div className={cn("p-4 space-y-2", className)}>
        <div className="h-4 bg-gray-200 rounded animate-pulse w-3/4" />
        <div className="h-4 bg-gray-200 rounded animate-pulse w-1/2" />
      </div>
    );
  }

  // If no scan history yet
  if (!scans || scans.length === 0) {
    return (
      <div className={cn("p-4", className)}>
        <div className="text-center py-4 text-muted-foreground flex flex-col items-center">
          <Clock className="w-6 h-6 mb-2 opacity-50" />
          <p className="text-sm">{t("products.noScansYet")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={cn("p-4 space-y-2", className)}>
      <h3 className="text-sm font-medium mb-2 text-amber-700">{t("products.recentScans")}</h3>
      <div className="space-y-2">
        {scans.map((scan) => (
          <div
            key={scan.id}
            className={cn(
              "p-3 rounded-lg text-sm border",
              {
                "border-amber-200 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-800": scan.status === "completed" && scan.product_id,
                "border-gray-200 bg-gray-50 dark:bg-gray-800/20 dark:border-gray-700": scan.status === "completed" && !scan.product_id,
                "border-yellow-200 bg-yellow-50 dark:bg-yellow-900/20 dark:border-yellow-800": scan.status === "pending",
                "border-red-200 bg-red-50 dark:bg-red-900/20 dark:border-red-800": scan.status === "failed",
              }
            )}
          >
            <div className="flex items-center justify-between mb-2">
              <span className="font-medium truncate flex-1">
                {scan.products?.name || 
                 (scan.ocr_result ? t("products.textDetected") : t("products.noMatch"))}
              </span>
              <span className="text-xs text-muted-foreground">
                {scan.created_at ? format(new Date(scan.created_at), 'MMM d, HH:mm') : ''}
              </span>
            </div>
            
            {scan.products?.brand && (
              <p className="text-xs text-muted-foreground mb-2">{scan.products.brand}</p>
            )}
            
            <div className="flex gap-2 mt-2">
              <Button
                variant="ghost"
                size="sm"
                className="flex-1 hover:bg-amber-100 dark:hover:bg-amber-900/30"
                onClick={() => handleScanClick(scan)}
              >
                <Search className="w-4 h-4 mr-1" />
                {scan.product_id ? t("products.viewProduct") : t("products.search")}
              </Button>
              
              {!scan.product_id && scan.ocr_result && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="flex-1 hover:bg-amber-100 dark:hover:bg-amber-900/30"
                  onClick={() => handleAddProduct(scan)}
                >
                  <Plus className="w-4 h-4 mr-1" />
                  {t("products.addProduct")}
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ScanHistory;
